import { isImmutable, List, Map } from 'immutable';

import { shapeFoldersForApp, shapeImagesForApp } from '../utils/studio/shapeForApp';
import { shapePhotoFileForEditor } from '../utils/photoEditor/state';
import { createController } from '../utils/controller';
import { downloadFile } from '../utils/files';
import { getFilters } from '../utils/studio/filters';
import { increase } from '../utils/math';
import { getSize } from '../utils/iterable/getSize';
import api from '../utils/api';

import { IMAGES_PER_PAGE, ORDER_BY } from '../constants/studio';
import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { EMPTY, SEPARATOR } from '../constants';
import { ETSY, SHOPIFY } from '../constants/channels';
import { FEATURE } from '../constants/features';

import actions from '../actions';

const imagesController = createController();
const foldersController = createController();

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function abortAll() {
  [
    imagesController,
    foldersController,
  ].forEach(abort);
}

function getShopParams(state) {
  const channel = state.getIn(['studio', 'channel']);
  const db = state.getIn(['studio', 'db']);
  const shopId = state.getIn(['studio', 'shopId']);
  return { channel, db, shopId };
}

function* bootstrap(reduction, { channel, db, shopId }) {
  abortAll();

  let state = Map({ channel, db, shopId });

  yield function effect(dispatch) {
    dispatch(actions.studio.photo.getFolders());
    dispatch(actions.studio.photo.getItems());
  };

  if (reduction.hasIn(['photo', 'uploading'])) {
    state = state.set('uploading', reduction.getIn(['photo', 'uploading']));
  }

  return reduction.set('photo', state);
}

function* cleanUp(reduction) {
  abortAll();

  let state = EMPTY.MAP;

  if (reduction.hasIn(['photo', 'uploading'])) {
    state = state.set('uploading', reduction.getIn(['photo', 'uploading']));
  }

  return reduction.set('photo', state);
}

function* createFolder(reduction, { callback, name }) {
  const { db, shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    api.studio.photo.createFolder({ db, name, shopId })
      .then(
        ({ uuid }) => dispatch(actions.studio.photo.createFolder.fulfilled({ callback, name, uuid })),
        (error) => dispatch(actions.studio.photo.createFolder.rejected(error)),
      );
  };

  return reduction;
}

function* createFolderRejected(reduction, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.CREATE_FOLDER,
      })
    );
  };

  return reduction;
}

function* createFolderFulfilled(reduction, { callback, name, uuid }) {
  yield function effect(dispatch) {
    if (typeof callback === 'function') {
      callback(uuid);
    } else {
      dispatch(
        actions.notifications.add({
          type: NOTIFICATION.SUCCESS,
          message: MESSAGE.SUCCESS.CREATE_FOLDER,
        })
      );
    }

    dispatch(actions.data.setData({ path: 'lastInteraction' }));
    dispatch(actions.studio.photo.getFolders());
    dispatch(actions.studio.photo.getItems({ folder: uuid }));
  };

  function updateById(byId) {
    return byId.set(uuid, Map({ count: 0, name, uuid }));
  }

  function updateIds(ids) {
    return ids.push(uuid);
  }

  return reduction
    .updateIn(['photo', 'folders', 'byId'], updateById)
    .updateIn(['photo', 'folders', 'ids'], updateIds);
}

function* deleteFolder(reduction, folder) {
  const { db, shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    api.studio.photo.deleteFolder({ db, folder, shopId })
      .then(
        () => dispatch(actions.studio.photo.deleteFolder.fulfilled(folder)),
        (error) => dispatch(actions.studio.photo.deleteFolder.rejected(error)),
      );
  };

  return reduction;
}

function* deleteFolderRejected(reduction, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.DELETE_FOLDER,
      })
    );
  };

  return reduction;
}

function* deleteFolderFulfilled(reduction, uuid) {
  yield function effect(dispatch) {
    dispatch(actions.data.setData({ path: 'lastInteraction' }));
    dispatch(actions.studio.photo.getFolders());
    dispatch(actions.studio.photo.getItems({ folder: null }));
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.SUCCESS,
        message: MESSAGE.SUCCESS.DELETE_FOLDER,
      })
    );
  };

  function updateIds(ids) {
    return ids.filter(function filterIds(item) {
      return item !== uuid;
    });
  }

  return reduction
    .deleteIn(['photo', 'folders', 'byId', uuid])
    .updateIn(['photo', 'folders', 'ids'], updateIds);
}

function* deleteItems(reduction, { uuids }) {
  const { db, shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    dispatch(
      actions.studio.photo.toggleImageState({
        images: uuids,
        state: 'processing',
        value: true,
      })
    );

    api.studio.photo.deleteImages({ db, shopId, uuids })
      .then(
        () => dispatch(actions.studio.photo.deleteItems.fulfilled(shopId)),
        (error) => dispatch(actions.studio.photo.deleteItems.rejected({ error, shopId })),
      );
  };

  return reduction;
}

function* deleteItemsRejected(reduction, { error, shopId }) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        message: MESSAGE.FAIL.DELETE_PHOTOS,
        type: NOTIFICATION.ERROR,
      })
    );

    if (shopId === getShopParams(reduction).shopId) {
      dispatch(actions.data.setData({ path: 'lastInteraction' }));
      dispatch(actions.studio.photo.getFolders());
      dispatch(actions.studio.photo.getItems({ clear: true }));
    }
  };

  return reduction;
}

function* deleteItemsFulfilled(reduction, shopId) {
  yield function effect(dispatch) {
    dispatch(
      actions.notifications.add({
        message: MESSAGE.SUCCESS.DELETE_PHOTOS,
        type: NOTIFICATION.SUCCESS,
      })
    );

    if (shopId === getShopParams(reduction).shopId) {
      dispatch(actions.data.setData({ path: 'lastInteraction' }));
      dispatch(actions.studio.photo.getFolders());
      dispatch(actions.studio.photo.getItems({ clear: true }));
    }
  };

  return reduction;
}

function* downloadItems(reduction, { name, uuids }) {
  const { db, shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    dispatch(
      actions.studio.photo.toggleImageState({
        images: uuids,
        state: 'processing',
        value: true,
      })
    );

    api.studio.photo.downloadImages({ db, shopId, uuids })
      .then((blob) => new Promise(function executor(resolve, reject) {
        const reader = new FileReader();

        reader.addEventListener('loadend', function onReaderLoad() {
          try {
            downloadFile({ data: reader.result, name });
            resolve();
          } catch (error) {
            reject(error);
          }
        });

        reader.readAsDataURL(blob);
      }))
      .then(
        () => dispatch(actions.studio.photo.downloadItems.fulfilled({ shopId, uuids })),
        (error) => dispatch(actions.studio.photo.downloadItems.rejected({ error, shopId, uuids })),
      );
  };

  return reduction;
}

function* downloadItemsRejected(reduction, { error, shopId, uuids }) {
  yield function effect(dispatch) {
    console.error(error);

    dispatch(
      actions.notifications.add({
        message: MESSAGE.FAIL.DOWNLOAD_PHOTOS,
        type: NOTIFICATION.ERROR,
      })
    );

    if (shopId === getShopParams(reduction).shopId) {
      dispatch(
        actions.studio.photo.toggleImageState({
          images: uuids,
          state: 'processing',
          value: false,
        })
      );
    }
  };

  return reduction;
}

function* downloadItemsFulfilled(reduction, { shopId, uuids }) {
  if (shopId === getShopParams(reduction).shopId) {
    yield function effect(dispatch) {
      dispatch(
        actions.studio.photo.toggleImageState({
          images: uuids,
          state: 'processing',
          value: false,
        })
      );
    };
  }

  return reduction;
}

function* getFolders(reduction) {
  const { db, shopId } = getShopParams(reduction);
  const { signal } = foldersController.start();

  yield function effect(dispatch) {
    api.studio.photo.getFolders({ db, shopId, signal })
      .then(
        (response) => dispatch(actions.studio.photo.getFolders.fulfilled(response)),
        (error) => dispatch(actions.studio.photo.getFolders.rejected({ error, signal })),
      );
  };

  return reduction;
}

function* getFoldersRejected(reduction, { error, signal }) {
  if (signal.aborted) return reduction;

  abort(foldersController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction
    .setIn(['photo', 'folders'], Map({ byId: EMPTY.MAP, ids: EMPTY.LIST }))
    .setIn(['photo', 'loaded'], reduction.hasIn(['photo', 'items']));
}

function* getFoldersFulfilled(reduction, response) {
  abort(foldersController);

  let state = reduction.get('photo');
  const allPhotos = Map({ count: parseInt(response.image_count) || 0, name: 'All photos' });
  const folders = response.folders.reduce(
    shapeFoldersForApp,
    state
      .get('folders', Map({ selected: null }))
      .set('byId', EMPTY.MAP.set(null, allPhotos))
      .set('ids', List([null]))
  );

  state = state
    .set('folders', folders)
    .set('loaded', state.has('items'));

  if (state.get('loaded') && !state.getIn(['request', 'folder'])) {
    state = state.setIn(['request', 'folder'], folders.getIn(['byId', folders.get('selected')]));
  }

  return reduction.set('photo', state);
}

function* getItems(reduction, payload = {}) {
  const { signal } = imagesController.start();
  const { db, shopId } = getShopParams(reduction);
  let state = reduction.get('photo').setIn(['request', 'processing'], true);
  const {
    folder = state.getIn(['request', 'folder', 'uuid'], null),
    order = { by: state.getIn(['order', 'by'], null), type: state.getIn(['order', 'type'], false) },
  } = payload;

  let {
    clear = false,
    page = state.getIn(['request', 'page'], 0),
    query = state.getIn(['request', 'query'], ''),
  } = payload;

  if (state.has('folders') && folder !== state.getIn(['folders', 'selected'])) {
    state = state.setIn(['folders', 'selected'], folder);
  }

  if (folder !== state.getIn(['request', 'folder', 'uuid'], null)) {
    clear = true;
    query = '';
  } else if (query !== state.getIn(['request', 'query'], '')) {
    clear = true;
    state = state.set('query', query);
  } else if (order.by !== state.getIn(['order', 'by'], null) || order.type !== state.getIn(['order', 'type'], false)) {
    clear = true;

    if (order.by) {
      state = state
        .setIn(['order', 'by'], order.by)
        .setIn(['order', 'type'], order.type);
    } else {
      order.by = undefined;
      order.type = undefined;
      state = state
        .deleteIn(['order', 'by'])
        .deleteIn(['order', 'type']);
    }
  }

  if (!order.by) {
    order.by = ORDER_BY.LISTINGS_CONNECTED;
    order.type = false;
    state = state
      .setIn(['order', 'by'], order.by)
      .setIn(['order', 'type'], order.type);
  }

  if (clear) {
    state = state.setIn(['items', 'loading'], true);
    page = 0;
  }

  const request = Map({
    folder: state.getIn(['folders', 'byId', folder]),
    order: Map(order),
    page,
    query,
  });

  yield function effect(dispatch) {
    api.studio.photo.getImages({ db, filters: getFilters({ order, page, query }), folder, shopId, signal })
      .then(
        (response) => dispatch(actions.studio.photo.getItems.fulfilled({ clear, request, response })),
        (error) => dispatch(actions.studio.photo.getItems.rejected({ error, request, signal })),
      );
  };

  return reduction.set('photo', state);
}

function* getItemsRejected(reduction, { error, request, signal }) {
  if (signal.aborted) return reduction;

  abort(imagesController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  const state = reduction
    .get('photo')
    .set('items', Map({ byId: EMPTY.MAP, ids: EMPTY.LIST, total: 0 }))
    .set('request', request);

  return reduction.set('photo', state.set('loaded', state.has('folders')));
}

function* getItemsFulfilled(reduction, { clear, request, response }) {
  abort(imagesController);

  let state = reduction.get('photo');
  let images = state
    .get('items', Map({ byId: EMPTY.MAP, ids: EMPTY.LIST }))
    .delete('loading')
    .delete('processing')
    .set('total', parseInt(response.count));

  if (clear) {
    images = images
      .delete('selected')
      .set('byId', EMPTY.MAP)
      .set('ids', EMPTY.LIST);
  }

  state = state
    .set('items', response.images.reduce(shapeImagesForApp, images))
    .set('loaded', state.has('folders'))
    .set('request', request);

  if ((request.get('page') + 1) * IMAGES_PER_PAGE < images.get('total')) {
    state = state.setIn(['request', 'hasMore'], true);
  }

  if (state.get('loaded') && !request.get('folder') && getSize(state.getIn(['folders', 'byId']))) {
    state = state.setIn(['request', 'folder'],
      state.getIn(['folders', 'byId', state.getIn(['folders', 'selected'], null)])
    );
  }

  return reduction.set('photo', state);
}

function* moveItems(reduction, { source, target, uuids }) {
  if (source === target || !Array.isArray(uuids) || !getSize(uuids)) return reduction;

  const { db, shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    api.studio.photo.moveImages({ db, shopId, source, target, uuids })
      .then(
        () => dispatch(actions.studio.photo.moveItems.fulfilled({ target, uuids })),
        (error) => dispatch(actions.studio.photo.moveItems.rejected(error)),
      );
  };

  return reduction;
}

function* moveItemsRejected(reduction, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.MOVE_PHOTOS,
      })
    );
  };

  return reduction;
}

function* moveItemsFulfilled(reduction, { target, uuids }) {
  const { shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    dispatch(actions.data.setData({ path: 'lastInteraction' }));
    dispatch(actions.studio.photo.getFolders());

    if (reduction.getIn(['photo', 'folders', 'selected']) || !target) {
      dispatch(actions.studio.photo.getItems({ clear: true }));
    }

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.PHOTOS_MOVED,
        folder: Map({
          title: reduction.getIn(['photo', 'folders', 'byId', target, 'name']),
          uuid: target,
        }),
        shopId,
      })
    );
  };

  function reduceSelectedImages(result, uuid) {
    return result.remove(uuid);
  }

  const path = ['photo', 'items', 'selected'];
  const selectedImages = uuids.reduce(reduceSelectedImages, reduction.getIn(path, EMPTY.SET));

  return reduction.setIn(path, selectedImages);
}

function* renameFolder(reduction, { folder, name }) {
  const { db, shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    api.studio.photo.renameFolder({ db, folder, name, shopId })
      .then(
        () => dispatch(actions.studio.photo.renameFolder.fulfilled({ name, uuid: folder })),
        (error) => dispatch(actions.studio.photo.renameFolder.rejected(error)),
      );
  };

  return reduction;
}

function* renameFolderRejected(reduction, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.RENAME_FOLDER,
      })
    );
  };

  return reduction;
}

function* renameFolderFulfilled(reduction, { name, uuid }) {
  yield function effect(dispatch) {
    dispatch(actions.data.setData({ path: 'lastInteraction' }));
    dispatch(actions.studio.photo.getFolders());
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.SUCCESS,
        message: MESSAGE.SUCCESS.RENAME_FOLDER,
      })
    );
  };

  function updateFolder(folder) {
    return folder.set('name', name);
  }

  return reduction.updateIn(['photo', 'folders', 'byId', uuid], updateFolder);
}

function* saveImage(state, { channel, db, folder, photo, shopId }) {
  yield function effect(dispatch) {
    const mediaLibrary = state.get('photo');
    const uuid = photo.get('uuid');

    if (!mediaLibrary.hasIn(['items', 'processing', uuid])) {
      dispatch(
        actions.studio.photo.toggleImageState({
          images: uuid,
          state: 'processing',
          value: true,
        })
      );
    }

    if (!mediaLibrary.hasIn(['items', 'byId', uuid])) {
      dispatch(
        actions.studio.photo.setValue({
          path: ['items', 'byId', uuid],
          value: photo,
        })
      );

      dispatch(
        actions.studio.photo.setValue({
          path: ['items', 'ids'],
          value: mediaLibrary.getIn(['items', 'ids'], EMPTY.LIST).unshift(uuid),
        })
      );
    }

    function onFail(error) {
      dispatch(actions.studio.photo.saveImage.rejected({ error, folder, shopId }));
    }

    function updatePhoto(updated) {
      dispatch(actions.studio.photo.saveImage({ channel, db, folder, photo: updated, shopId }));
    }

    function onUploaded({ url }) {
      updatePhoto(photo.set('url', url));
    }

    const url = photo.get('url');

    if (!url) {
      const data = photo.get('data');
      const mime = photo.get('mime');

      api.studio.photo.uploadToS3({ data, db, mime, shopId })
        .then(onUploaded)
        .catch(onFail);

      return;
    }

    function onGenerated({ altText }) {
      updatePhoto(photo.set('altText', altText.trim().replace(/\s+/g, SEPARATOR.SPACE)));
    }

    const altText = photo.get('altText');
    const fileName = photo.get('fileName');
    const format = photo.get('format');

    switch (channel) {
      case ETSY:
      case SHOPIFY: {
        if (!photo.get('altText')) {
          const v2 = state.getIn(['user', 'features', FEATURE.AI_V2]);

          api.ai.generateAltText({ image: url, v2, channel, db, shopId })
            .then(onGenerated)
            .catch(onFail);

          return;
        }

        break;
      }

      default: {
        break;
      }
    }

    function onSuccess() {
      dispatch(actions.studio.photo.saveImage.fulfilled({ folder, shopId }));
    }

    api.studio.photo
      .savePhoto({ altText, db, folder, format, name: fileName, shopId, url })
      .then(onSuccess)
      .catch(onFail);
  };

  return state;
}

function* saveImageFulfilled(state, { folder, shopId }) {
  yield function effect(dispatch) {
    if (shopId === getShopParams(state).shopId) {
      dispatch(actions.studio.photo.getFolders());

      if (
        !state.getIn(['request', 'processing']) &&
        folder === state.getIn(['request', 'folder', 'uuid'])
      ) {
        dispatch(actions.data.setData({ path: 'lastInteraction' }));
        dispatch(actions.studio.photo.getItems({ clear: true }));
      }
    }
  };

  return state;
}

function* saveImageRejected(state, { error, folder, shopId }) {
  yield function effect(dispatch) {
    dispatch(
      actions.notifications.add({
        message: MESSAGE.FAIL.SAVE_PHOTO,
        type: NOTIFICATION.ERROR,
      })
    );

    if (error) {
      console.error(error);
    }

    if (shopId === getShopParams(state).shopId) {
      dispatch(actions.studio.photo.getFolders());

      if (
        !state.getIn(['photo', 'request', 'processing']) &&
        folder === state.getIn(['photo', 'request', 'folder', 'uuid'])
      ) {
        dispatch(actions.data.setData({ path: 'lastInteraction' }));
        dispatch(actions.studio.photo.getItems({ clear: true }));
      }
    }
  };

  return state;
}

function* setValue(reduction, { path, value }) {
  if (!path) return reduction;

  const fullPath = Array.isArray(path)
    ? ['photo', ...path]
    : ['photo', path];

  return value === undefined
    ? reduction.deleteIn(fullPath)
    : reduction.setIn(fullPath, value);
}

function* toggleImageState(reduction, { images, state: path, value }) {
  function reduceImageIds(result, uuid) {
    switch (value) {
      case true: {
        return result.add(uuid);
      }

      case false: {
        return result.remove(uuid);
      }

      default: {
        return result.has(uuid)
          ? result.remove(uuid)
          : result.add(uuid);
      }
    }
  }

  const fullPath = ['photo', 'items', path];
  const state = typeof images === 'string'
    ? reduceImageIds(reduction.getIn(fullPath, EMPTY.SET), images)
    : Array.isArray(images) || isImmutable(images)
      ? images.reduce(reduceImageIds, reduction.getIn(fullPath, EMPTY.SET))
      : null;

  return getSize(state)
    ? reduction.setIn(fullPath, state)
    : reduction.deleteIn(fullPath);
}

function* updateImage(state, { channel, db, folder, photo, shopId }) {
  yield function effect(dispatch) {
    const mediaLibrary = state.get('photo');
    const uuid = photo.get('uuid');

    if (!mediaLibrary.hasIn(['items', 'processing', uuid])) {
      dispatch(
        actions.studio.photo.toggleImageState({
          images: uuid,
          state: 'processing',
          value: true,
        })
      );
    }

    function onFail(error) {
      dispatch(actions.studio.photo.updateImage.rejected({ error, folder, shopId }));
    }

    function updatePhoto(updated) {
      dispatch(actions.studio.photo.updateImage({ channel, db, folder, photo: updated, shopId }));
    }

    function onUploaded({ url }) {
      updatePhoto(photo.set('url', url));
    }

    const url = photo.get('url');

    if (!url) {
      const data = photo.get('data');
      const mime = photo.get('mime');

      api.studio.photo.uploadToS3({ data, db, mime, shopId })
        .then(onUploaded)
        .catch(onFail);

      return;
    }

    function onGenerated({ altText }) {
      updatePhoto(photo.set('altText', altText.trim().replace(/\s+/g, SEPARATOR.SPACE)));
    }

    const altText = photo.get('altText');
    const fileName = photo.get('fileName');
    const format = photo.get('format');

    switch (channel) {
      case ETSY:
      case SHOPIFY: {
        if (!photo.get('altText')) {
          const v2 = state.getIn(['user', 'features', FEATURE.AI_V2]);

          api.ai.generateAltText({ image: url, v2, channel, db, shopId })
            .then(onGenerated)
            .catch(onFail);

          return;
        }

        break;
      }

      default: {
        break;
      }
    }

    function onSuccess() {
      dispatch(actions.studio.photo.updateImage.fulfilled({ folder, shopId }));
    }

    dispatch(
      actions.studio.photo.setValue({
        path: ['items', 'byId', uuid],
        value: photo,
      })
    );

    const isAltTextOnly =
      altText !== undefined &&
      fileName === undefined &&
      format === undefined;

    const request = isAltTextOnly
      ? api.studio.photo.updatePhotoAltText({ altText, db, shopId, uuid })
      : api.studio.photo.updatePhoto({ altText, db, folder, format, name: fileName, shopId, url, uuid });

    request.then(onSuccess).catch(onFail);
  };

  return state;
}

function* updateImageFulfilled(state, { folder, shopId }) {
  yield function effect(dispatch) {
    if (shopId === getShopParams(state).shopId) {
      dispatch(actions.studio.photo.getFolders());

      if (
        !state.getIn(['request', 'processing']) &&
        folder === state.getIn(['request', 'folder', 'uuid'])
      ) {
        dispatch(actions.data.setData({ path: 'lastInteraction' }));
        dispatch(actions.studio.photo.getItems({ clear: true }));
      }
    }
  };

  return state;
}

function* updateImageRejected(state, { error, folder, shopId }) {
  yield function effect(dispatch) {
    dispatch(
      actions.notifications.add({
        message: MESSAGE.FAIL.SAVE_PHOTO,
        type: NOTIFICATION.ERROR,
      })
    );

    if (error) {
      console.error(error);
    }

    if (shopId === getShopParams(state).shopId) {
      dispatch(actions.studio.photo.getFolders());

      if (
        !state.getIn(['photo', 'request', 'processing']) &&
        folder === state.getIn(['photo', 'request', 'folder', 'uuid'])
      ) {
        dispatch(actions.data.setData({ path: 'lastInteraction' }));
        dispatch(actions.studio.photo.getItems({ clear: true }));
      }
    }
  };

  return state;
}

function* uploadFiles(reduction, { files }) {
  const current = getShopParams(reduction);
  let state = reduction.get('photo');

  if (getSize(files) === 0) {
    const folder = state.getIn(['uploading', 'folder']);
    const count = state.getIn(['uploading', 'count']);
    const shopId = state.getIn(['uploading', 'shopId']);
    const total = state.getIn(['uploading', 'total']);

    if (total) {
      yield function effect(dispatch) {
        if (!count) {
          dispatch(
            actions.notifications.add({
              message: MESSAGE.FAIL.UPLOAD_FILES,
              type: NOTIFICATION.ERROR,
            })
          );

          return;
        }

        dispatch(
          actions.notifications.add({
            message: count === total
              ? MESSAGE.SUCCESS.UPLOAD_FILES
              : `${count} of ${total} files uploaded`,
            type: NOTIFICATION.SUCCESS,
          })
        );

        if (shopId === current.shopId) {
          dispatch(actions.studio.photo.getFolders());

          if (
            !state.getIn(['request', 'processing']) &&
            folder === state.getIn(['request', 'folder', 'uuid'])
          ) {
            dispatch(actions.data.setData({ path: 'lastInteraction' }));
            dispatch(actions.studio.photo.getItems({ clear: true }));
          }
        }
      };
    }

    return reduction.set('photo', state.delete('uploading'));
  }

  if (!state.get('uploading')) {
    const { channel, db, shopId } = current;
    const folder = state.getIn(['request', 'folder', 'uuid']);
    const total = getSize(files);
    const uploading = Map({
      channel,
      count: 0,
      db,
      folder,
      images: Map({ byId: EMPTY.MAP, ids: EMPTY.LIST }),
      shopId,
      total,
    });

    state = state.setIn(['uploading'], uploading);
  }

  yield function effect(dispatch) {
    const file = files.first();
    const channel = state.getIn(['uploading', 'channel']);
    shapePhotoFileForEditor({ channel, file })
      .then(
        (photo) => dispatch(actions.studio.photo.uploadFiles.fulfilled({ files, photo })),
        (error) => dispatch(actions.studio.photo.uploadFiles.rejected({ error, files })),
      );
  };

  return reduction.set('photo', state);
}

function* uploadFilesRejected(reduction, { error, files, photo }) {
  let state = reduction.get('photo');

  yield function effect(dispatch) {
    console.error(error);
    dispatch(actions.studio.photo.uploadFiles({ files: files.shift() }));
  };

  function updateIds(ids) {
    return ids.filter(function filterIds(id) {
      return id !== photo.id;
    });
  }

  if (photo) {
    state = state
      .deleteIn(['uploading', 'items', 'byId', photo.id])
      .updateIn(['uploading', 'items', 'ids'], updateIds);
  }

  return reduction.set('photo', state);
}

function* uploadFilesFulfilled(reduction, { files, photo, uploaded }) {
  const current = getShopParams(reduction);
  let state = reduction.get('photo');

  if (uploaded) {
    const folder = state.getIn(['uploading', 'folder']);
    const shopId = state.getIn(['uploading', 'shopId']);

    if (shopId === current.shopId) {
      if (folder) {
        state = state
          .updateIn(['folders', 'byId', null, 'count'], increase)
          .updateIn(['folders', 'byId', folder, 'count'], increase);
      } else {
        state = state.updateIn(['folders', 'byId', null, 'count'], increase);
      }

      if (
        !state.getIn(['request', 'processing']) &&
        folder === state.getIn(['request', 'folder', 'uuid'])
      ) {
        const image = state
          .getIn(['uploading', 'items', 'byId', photo.id])
          .set('alt_text', uploaded.altText)
          .set('url', uploaded.url)
          .set('uuid', uploaded.uuid);

        state = state
          .updateIn(['items', 'total'], increase)
          .setIn(['items', 'byId', uploaded.uuid], image)
          .updateIn(['items', 'ids'], function updateIds(ids) {
            return ids.unshift(uploaded.uuid);
          });
      }
    }

    state = state
      .updateIn(['uploading', 'count'], increase)
      .deleteIn(['uploading', 'items', 'byId', photo.id])
      .updateIn(['uploading', 'items', 'ids'], function updateIds(ids) {
        return ids.filter(function filterIds(id) {
          return id !== photo.id;
        });
      });

    yield function effect(dispatch) {
      dispatch(actions.studio.photo.uploadFiles({ files: files.shift() }));
    };
  } else {
    const { data, format, id, mime, name, url: preview } = photo;
    state = state
      .updateIn(['uploading', 'items', 'byId'], function updateById(byId = EMPTY.MAP) {
        return byId.set(id,
          Map({
            fileName: name,
            format,
            mime,
            url: preview,
            uuid: id,
          })
        );
      })
      .updateIn(['uploading', 'items', 'ids'], function update(ids = EMPTY.LIST) {
        return ids.push(id);
      });

    yield function effect(dispatch) {
      const channel = state.getIn(['uploading', 'channel']);
      const db = state.getIn(['uploading', 'db']);
      const folder = state.getIn(['uploading', 'folder']);
      const shopId = state.getIn(['uploading', 'shopId']);

      function onFail(error) {
        dispatch(actions.studio.photo.uploadFiles.rejected({ error, files, photo }));
      }

      function onUploaded({ url }) {
        function savePhoto({ altText = '' } = {}) {
          function onSaved({ fileUuid: uuid }) {
            dispatch(
              actions.studio.photo.uploadFiles.fulfilled({
                files,
                photo,
                uploaded: {
                  altText,
                  url,
                  uuid,
                },
              })
            );
          }

          api.studio.photo.savePhoto({ altText, db, folder, format, name, shopId, url })
            .then(onSaved)
            .catch(onFail);
        }

        function onGenerated({ altText }) {
          savePhoto({ altText: altText.trim().replace(/\s+/g, SEPARATOR.SPACE) });
        }

        switch (channel) {
          case ETSY:
          case SHOPIFY: {
            if (!photo.alt_text) {
              const v2 = state.getIn(['user', 'features', FEATURE.AI_V2]);

              api.ai.generateAltText({ image: url, v2, channel, db, shopId })
                .then(onGenerated)
                .catch(onFail);
            } else {
              savePhoto();
            }

            break;
          }

          default: {
            savePhoto();
            break;
          }
        }
      }

      api.studio.photo.uploadToS3({ data, db, mime, shopId })
        .then(onUploaded)
        .catch(onFail);
    };
  }

  return reduction.set('photo', state);
}

const mediaLibrary = {
  [actions.studio.photo.bootstrap]: bootstrap,
  [actions.studio.photo.cleanUp]: cleanUp,
  [actions.studio.photo.createFolder]: createFolder,
  [actions.studio.photo.createFolder.fulfilled]: createFolderFulfilled,
  [actions.studio.photo.createFolder.rejected]: createFolderRejected,
  [actions.studio.photo.deleteFolder]: deleteFolder,
  [actions.studio.photo.deleteFolder.fulfilled]: deleteFolderFulfilled,
  [actions.studio.photo.deleteFolder.rejected]: deleteFolderRejected,
  [actions.studio.photo.deleteItems]: deleteItems,
  [actions.studio.photo.deleteItems.fulfilled]: deleteItemsFulfilled,
  [actions.studio.photo.deleteItems.rejected]: deleteItemsRejected,
  [actions.studio.photo.downloadItems]: downloadItems,
  [actions.studio.photo.downloadItems.fulfilled]: downloadItemsFulfilled,
  [actions.studio.photo.downloadItems.rejected]: downloadItemsRejected,
  [actions.studio.photo.getFolders]: getFolders,
  [actions.studio.photo.getFolders.fulfilled]: getFoldersFulfilled,
  [actions.studio.photo.getFolders.rejected]: getFoldersRejected,
  [actions.studio.photo.getItems]: getItems,
  [actions.studio.photo.getItems.fulfilled]: getItemsFulfilled,
  [actions.studio.photo.getItems.rejected]: getItemsRejected,
  [actions.studio.photo.moveItems]: moveItems,
  [actions.studio.photo.moveItems.fulfilled]: moveItemsFulfilled,
  [actions.studio.photo.moveItems.rejected]: moveItemsRejected,
  [actions.studio.photo.renameFolder]: renameFolder,
  [actions.studio.photo.renameFolder.fulfilled]: renameFolderFulfilled,
  [actions.studio.photo.renameFolder.rejected]: renameFolderRejected,
  [actions.studio.photo.saveImage]: saveImage,
  [actions.studio.photo.saveImage.fulfilled]: saveImageFulfilled,
  [actions.studio.photo.saveImage.rejected]: saveImageRejected,
  [actions.studio.photo.setValue]: setValue,
  [actions.studio.photo.toggleImageState]: toggleImageState,
  [actions.studio.photo.updateImage]: updateImage,
  [actions.studio.photo.updateImage.fulfilled]: updateImageFulfilled,
  [actions.studio.photo.updateImage.rejected]: updateImageRejected,
  [actions.studio.photo.uploadFiles]: uploadFiles,
  [actions.studio.photo.uploadFiles.fulfilled]: uploadFilesFulfilled,
  [actions.studio.photo.uploadFiles.rejected]: uploadFilesRejected,
};

export default mediaLibrary;
